// libraries
import React from 'react';
import { FormRenderProps, FormSpy } from 'react-final-form';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';

// custom components
import Form from 'components/Form';
import Select from 'components/Form/Select/Select';
import ProviderSignUpSuccess from './ProviderSignUpSuccess';
import TermsAcceptedCheckbox from 'modules/SignUpWithFooterContactForms/components/TermsAcceptedCheckbox';

// styled components
import { Flex, ColumnedFlex, Error, PrimaryButton } from 'shared/styledComponents';
import {
  InputsWrapper,
  PairedInputs,
  SingleInput,
  StyledInput,
  SelectStyles,
  SelectPlaceholder,
  FullWidthSelectStyles,
} from '../../sharedStyles';
import { H3, Text } from 'shared/typography';

// constants
import { STATES_OPTIONS, MATERNAL_FOCUS_OPTIONS } from '../constants';

// styles
import devices from 'theme/devices';

// utils
import { getValue } from 'utils/getValue';
import { validationMessages } from 'modules/LogInOrCreateAccount/CreateAccount/types';

const Wrapper = styled(Flex)`
  width: 50%;
  padding: 90px 0 65px;
  background-color: ${({ theme: { colors } }): string => colors.blueDianne};

  @media ${devices.mobile} {
    width: 100%;
    justify-content: center;
  }
`;

const Heading = styled(H3)`
  max-width: 450px;
  width: 100%;
  color: ${({ theme: { colors } }): string => colors.white};
  margin-bottom: 5px;
  @media ${devices.mobile} {
    max-width: 310px;
  }
`;

const SubHeading = styled(Text)`
  color: ${({ theme: { colors } }): string => colors.white};
  font-size: 16;
  margin-bottom: 10px;

  a {
    color: white;
  }
`;

const ServiceDisclaimer = styled(Text)`
  color: ${({ theme: { colors } }): string => colors.white};
  font-size: 10px;
`;

const NoMarginSingleInput = styled(SingleInput)`
  margin-bottom: 0px;
`;

const FormWrapper = styled(ColumnedFlex)`
  width: 100%;
  max-width: 570px;
  padding-left: 105px;

  @media ${devices.mobile} {
    width: fit-content;
    max-width: 310px;
    padding-left: 0;
  }
`;

const FormContentWrapper = styled(ColumnedFlex)`
  justify-content: center;
  align-items: flex-start;
  min-height: 415px;

  @media ${devices.mobile} {
    justify-content: center;
  }
`;

const StyledSubmitButton = styled(PrimaryButton)(
  ({ isLoading, theme: { colors } }) => `
    width: 100px;
    background-color: ${colors.greenGable};
    cursor: ${isLoading ? 'wait' : 'pointer'};
  `,
);

interface ProviderSignUpFormProps {
  serviceOptions?: any;
  identityOptions?: any;
  onSubmit: (values: any) => void;
  formValidation: (values: any) => any;
  handleAnalytics: (values: any) => any;
}

const ProviderSignUpForm = ({
  serviceOptions,
  onSubmit,
  formValidation,
  handleAnalytics,
  identityOptions,
}: ProviderSignUpFormProps): JSX.Element => {
  return (
    <Wrapper id="listYourPractice">
      <FormWrapper>
        <Form onSubmit={onSubmit} validate={formValidation}>
          {({
            values,
            form: { reset },
            submitting,
            submitSucceeded,
            submitFailed,
            submitErrors,
            hasValidationErrors,
            errors,
          }: FormRenderProps): JSX.Element => (
            <FormContentWrapper>
              <FormSpy
                subscription={{ dirtyFields: true }}
                onChange={(props) => {
                  handleAnalytics(props);
                }}
              />
              {submitSucceeded ? (
                <ProviderSignUpSuccess onClose={reset} />
              ) : (
                <>
                  <Heading>Take the first step to list your practice. Join our waitlist.</Heading>
                  <SubHeading>Step 1: Fill out our interest form below.</SubHeading>

                  <InputsWrapper>
                    <PairedInputs>
                      <StyledInput name="firstName" type="text" placeholder="First Name" size="small" />
                      <StyledInput name="lastName" type="text" placeholder="Last Name" size="small" />
                    </PairedInputs>
                    <PairedInputs>
                      <StyledInput name="email" type="email" placeholder="Email Address" size="small" />
                      <StyledInput name="city" type="text" placeholder="City" size="small" />
                    </PairedInputs>
                    <PairedInputs>
                      <Select
                        name="state"
                        options={STATES_OPTIONS}
                        customStyles={FullWidthSelectStyles}
                        placeholder={<SelectPlaceholder>State</SelectPlaceholder>}
                      />
                    </PairedInputs>
                    <NoMarginSingleInput>
                      <Select
                        name="services"
                        options={values.services?.length === 3 ? values.services : serviceOptions}
                        customStyles={FullWidthSelectStyles}
                        placeholder={<SelectPlaceholder>Service (multiple 3)*</SelectPlaceholder>}
                        isMulti
                      />
                    </NoMarginSingleInput>
                    <ServiceDisclaimer>
                      We are currently only accepting applicants with valid certifications.
                    </ServiceDisclaimer>
                    <SingleInput>
                      <Select
                        name="identities"
                        options={identityOptions}
                        customStyles={FullWidthSelectStyles}
                        placeholder={<SelectPlaceholder>Select your identity</SelectPlaceholder>}
                      />
                    </SingleInput>
                    <TermsAcceptedCheckbox />
                  </InputsWrapper>
                  {submitFailed && hasValidationErrors && (
                    <Error size="small">
                      {!!Object.keys(errors).length &&
                        `${getValue(Object.keys(errors)[0], validationMessages)} ${errors[Object.keys(errors)[0]]}`}
                    </Error>
                  )}
                  <SubHeading>
                    Step 2:{' '}
                    <a href="https://wearerobyn.typeform.com/to/rMjTSLFA" target="_blank">
                      Fill out an application
                    </a>
                  </SubHeading>
                  <SubHeading>Step 3: You&apos;ll hear from us within 4-6 weeks</SubHeading>
                  {submitFailed && submitErrors?.error && <Error size="small">{submitErrors?.error}</Error>}
                  <StyledSubmitButton type="submit" size="small" isLoading={submitting}>
                    Submit
                  </StyledSubmitButton>
                </>
              )}
            </FormContentWrapper>
          )}
        </Form>
      </FormWrapper>
    </Wrapper>
  );
};

export default ProviderSignUpForm;
